'use client';

import { useCallback, useEffect, useRef } from 'react';
import { usePathname, useRouter } from 'next/navigation';
import { useAppDispatch } from '@/hooks/redux';
import {
  requestProceedPopup,
  setLastCheckoutFile,
} from '@/store/slices/checkoutReturnSlice';
import { useUnsavedChangesPrompt } from '@/features/tool/hooks/useUnsavedChangesPrompt';

export const DOCUMENTS_ROUTE = '/documents';

type CheckoutReturnInput = {
  isEnabled: boolean;
  file: File | null;
  fileName: string;
  extension: string;
  toolType?: string;
  originEntryPoint?: string;
};

const isDocumentsPath = (pathname: string | null) =>
  Boolean(pathname && pathname.replace(/\/+$/, '').endsWith(DOCUMENTS_ROUTE));

export const useCheckoutReturn = ({
  isEnabled,
  file,
  fileName,
  extension,
  toolType,
  originEntryPoint,
}: CheckoutReturnInput) => {
  const dispatch = useAppDispatch();
  const router = useRouter();
  const pathname = usePathname();
  const { hasUnsavedChanges, discardChanges } = useUnsavedChangesPrompt();
  const isCapturedRef = useRef(false);
  const isReturnRequestedRef = useRef(false);

  useEffect(() => {
    if (!isEnabled || !file) {
      return;
    }

    dispatch(
      setLastCheckoutFile({
        file,
        fileName,
        extension,
        toolType,
        originEntryPoint,
      })
    );
    isCapturedRef.current = true;
  }, []);

  const requestReturn = useCallback(() => {
    if (isCapturedRef.current) {
      isReturnRequestedRef.current = true;
    }
  }, []);

  const runPendingReturn = useCallback(() => {
    if (!isReturnRequestedRef.current) {
      return;
    }

    isReturnRequestedRef.current = false;
    dispatch(requestProceedPopup());

    if (isDocumentsPath(pathname)) {
      return;
    }

    if (hasUnsavedChanges) {
      discardChanges();
    }

    router.push(DOCUMENTS_ROUTE);
  }, [discardChanges, dispatch, hasUnsavedChanges, pathname, router]);

  return { requestReturn, runPendingReturn };
};

export default useCheckoutReturn;
